import * as React from 'react';

import type {CoreEntityType} from 'nutshell-core/types';

import {Modal} from 'shells/modal/modal';

import type {
    CreateSavedFilter_filterCreate_filter as SavedFilter,
    CreateFixedAudience_audienceCreate_audience as Audience,
    GetSessionUser_loggedInUser as User,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import {appHistory} from '../../../history';
import {removeListParamFromValue} from '../../../saved-filters/saved-filters-utils';

import type {NavItemObj} from '../entity-list-sidebar/types';
import {
    useUpdateFilter,
    useCreateFilter,
    useCreateAudience,
    useUpdateAudienceForEntityList,
} from '../graphql/hooks';

import {getSavedFilterTypeForGraphQL} from './helpers';
import {SaveListModalForm} from './save-list-modal-form';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    navItem: NavItemObj;
    currentFilterString: string;
    entityType: CoreEntityType;
    searchNameQuery: string | null | undefined;
    currentSelectedId: string | null | undefined;
    createFilterRedux: (filter: SavedFilter) => void;
    updateFilterRedux: (filter: SavedFilter) => void;
    user: User;
    handleAudienceBulkEdit: (
        audience: Audience,
        isModalForCurrentList: boolean,
        navItem: NavItemObj
    ) => Promise<any>;
};

export function SaveListModal(props: Props) {
    const {updateFilter, isLoading: isUpdatingFilter} = useUpdateFilter();
    const {createFilter, isLoading: isCreatingFilter} = useCreateFilter(
        props.entityType,
        props.searchNameQuery
    );
    const {createAudience, isLoading: isCreatingAudience} = useCreateAudience(
        props.searchNameQuery
    );
    const {updateAudienceForEntityList, isLoading: isUpdatingAudience} =
        useUpdateAudienceForEntityList();

    const isCopying = Boolean((isCreatingFilter || isCreatingAudience) && props.navItem.id);
    const isSaving =
        Boolean((isCreatingFilter || isCreatingAudience) && !props.navItem.id) ||
        isUpdatingFilter ||
        isUpdatingAudience;

    const listEntityType = getSavedFilterTypeForGraphQL(props.entityType);
    const isModalForCurrentList = !props.navItem.id
        ? true // we must be on the unsaved list if we don't have an id
        : props.navItem.id === props.currentSelectedId;

    const headerText = getHeaderText(props.navItem);

    const handleUpdateFilter = (
        name: string,
        description: string | null | undefined,
        isShared: boolean
    ) => {
        if (props.navItem.id) {
            updateFilter({
                id: props.navItem.id,
                name,
                isShared,
                description,
                value: isModalForCurrentList ? props.currentFilterString : undefined,
            }).then((res) => {
                if (res && res.data && res.data.filterUpdate && res.data.filterUpdate.filter) {
                    props.updateFilterRedux(res.data.filterUpdate.filter);
                }
            });
        }
    };

    const handleCreateFilter = (
        name: string,
        description: string | null | undefined,
        isShared: boolean
    ) => {
        let value = props.currentFilterString;
        if (!isModalForCurrentList && props.navItem.id && props.navItem.link) {
            const [, queryString] = props.navItem.link.split('?');
            if (queryString) {
                value = removeListParamFromValue(queryString);
            }
        }

        createFilter({
            name,
            isShared,
            description,
            type: listEntityType,
            value,
        }).then((res) => {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_SAVED_LIST_CREATED_ON_ENTITY_LIST_PAGE,
                {
                    entity: props.entityType,
                }
            );

            if (res && res.data && res.data.filterCreate && res.data.filterCreate.filter) {
                props.createFilterRedux(res.data.filterCreate.filter);
            }
        });
    };

    const handleCreateAudience = (name: string, description?: string | null) => {
        createAudience({
            audience: {
                name,
                description,
            },
        }).then((res) => {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_AUDIENCE_CREATED_ON_ENTITY_LIST_PAGE
            );

            // createAudience mutation is fast but close model before bulk edit
            // Adding members for large audiences can take a while
            // Timeout serves no functional purpose just smoother UI if modal does not close immediately
            setTimeout(() => {
                props.onClose();
            }, 1000);

            if (res && res.data && res.data.audienceCreate && res.data.audienceCreate.audience) {
                const audience = res.data.audienceCreate.audience;
                appHistory.replace(audience.htmlUrlPathForList);
                props.handleAudienceBulkEdit(audience, isModalForCurrentList, props.navItem);
            }
        });
    };

    const handleUpdateAudience = (name: string, description?: string | null) => {
        if (props.navItem.id) {
            updateAudienceForEntityList({
                audienceId: props.navItem.id,
                name,
                description,
            }).then(() => {
                setTimeout(() => {
                    props.onClose();
                }, 1000);
            });
        }
    };

    const canAccessFullCampaigns = props.user.permissions.canAccessFullCampaigns;
    const canAccessMarketing = props.user.permissions.canAccessMarketing;
    const isLimitedUser = !canAccessFullCampaigns && canAccessMarketing;

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            headerText={headerText}
            maxWidth={500}
            fitHeight={true}
            closeButton={false}
        >
            <SaveListModalForm
                navItem={props.navItem}
                entityType={props.entityType}
                onUpdateFilter={handleUpdateFilter}
                onCreateFilter={handleCreateFilter}
                onUpdateAudience={handleUpdateAudience}
                onCreateAudience={handleCreateAudience}
                onClose={props.onClose}
                isCopying={isCopying}
                isSaving={isSaving}
                canAccessMarketing={canAccessMarketing}
                isLimitedMarketingUser={isLimitedUser}
            />
        </Modal>
    );
}

function getHeaderText(navItem: NavItemObj): string {
    if (navItem.id) {
        return navItem.isImmutable ? 'Save a new copy' : 'Edit';
    }

    return `Save ${navItem.type}`;
}
